import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Lightbulb, Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";

interface BriefNote {
  id: string;
  user_id: string;
  section_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_email?: string;
  display_name?: string;
}

interface BriefNotesListProps {
  challengeId: string;
  sectionId: string;
  isTeamMember: boolean;
  isAdmin: boolean;
}

const BriefNotesList: React.FC<BriefNotesListProps> = ({
  challengeId,
  sectionId,
  isTeamMember,
  isAdmin,
}) => {
  const [notes, setNotes] = useState<BriefNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  useEffect(() => {
    fetchNotes();
    checkUser();
  }, [challengeId, sectionId]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from("challenge_brief_notes")
        .select(`
          id,
          user_id,
          section_id,
          content,
          created_at,
          updated_at
        `)
        .eq("challenge_id", challengeId)
        .eq("section_id", sectionId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Fetch user info for each note
      const notesWithUsers = await Promise.all(
        (data || []).map(async (note) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("email")
            .eq("id", note.user_id)
            .maybeSingle();

          const { data: contributor } = await supabase
            .from("contributor_profiles")
            .select("display_name")
            .eq("id", note.user_id)
            .maybeSingle();

          return {
            ...note,
            user_email: profile?.email,
            display_name: contributor?.display_name,
          };
        })
      );

      setNotes(notesWithUsers);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim() || !currentUserId) return;
    
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("challenge_brief_notes")
        .insert({
          challenge_id: challengeId,
          section_id: sectionId,
          user_id: currentUserId,
          content: newNoteContent.trim(),
        });

      if (error) throw error;

      toast.success("Note added");
      setNewNoteContent("");
      setShowAddForm(false);
      fetchNotes();
    } catch (error: any) {
      console.error("Error adding note:", error);
      toast.error(error.message || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editContent.trim()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("challenge_brief_notes")
        .update({ content: editContent.trim() })
        .eq("id", noteId);

      if (error) throw error;

      toast.success("Note updated");
      setEditingNoteId(null);
      setEditContent("");
      fetchNotes();
    } catch (error: any) {
      console.error("Error updating note:", error);
      toast.error("Failed to update note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      const { error } = await supabase
        .from("challenge_brief_notes")
        .delete()
        .eq("id", noteId);

      if (error) throw error;

      toast.success("Note deleted");
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const getInitials = (email?: string, displayName?: string) => {
    if (displayName) {
      return displayName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return "??";
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading insights...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lightbulb className="h-4 w-4" />
          <span>Team Insights {notes.length > 0 && `(${notes.length})`}</span>
        </div>
        {isTeamMember && !showAddForm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAddForm(true)}
            className="h-7 text-xs gap-1"
          >
            <Plus className="h-3 w-3" />
            Add Note
          </Button>
        )}
      </div>

      {/* Add Note Form */}
      {showAddForm && (
        <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
          <Textarea
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            placeholder="Share an insight, discovery, or note for others working on this challenge..."
            className="min-h-[80px] text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddForm(false);
                setNewNoteContent("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!newNoteContent.trim() || submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Note"}
            </Button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 && !showAddForm ? (
        <p className="text-sm text-muted-foreground italic">
          No team insights yet. {isTeamMember && "Be the first to share!"}
        </p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="p-3 bg-muted/30 rounded-lg border border-border/50"
            >
              {editingNoteId === note.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[60px] text-sm"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingNoteId(null);
                        setEditContent("");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleUpdateNote(note.id)}
                      disabled={!editContent.trim() || submitting}
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {getInitials(note.user_email, note.display_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {note.display_name || note.user_email || "Anonymous"}
                        </span>
                        <span>·</span>
                        <span>
                          {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{note.content}</p>
                    </div>
                  </div>
                  {(currentUserId === note.user_id || isAdmin) && (
                    <div className="flex justify-end gap-1 mt-2">
                      {currentUserId === note.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            setEditingNoteId(note.id);
                            setEditContent(note.content);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BriefNotesList;
